import React, { useState, useEffect } from 'react';

interface TimeSlot {
  label: string;
  value: string;
  start: string;
  end: string;
  available: boolean;
}

interface DayAvailability {
  date: string;
  timeSlots: TimeSlot[];
}

interface CalendarDay {
  date: string;
  day: number;
  isToday: boolean;
  isPast: boolean;
}

interface AvailabilityCalendarProps {
  value?: DayAvailability[];
  onChange?: (availability: DayAvailability[]) => void;
  readOnly?: boolean;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  value = [],
  onChange,
  readOnly = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [availability, setAvailability] = useState<DayAvailability[]>(value);

  const timeSlots = [
    { label: '9:00 AM', value: '09:00', start: '09:00', end: '10:00', available: false },
    { label: '10:00 AM', value: '10:00', start: '10:00', end: '11:00', available: false },
    { label: '11:00 AM', value: '11:00', start: '11:00', end: '12:00', available: false },
    { label: '12:00 PM', value: '12:00', start: '12:00', end: '13:00', available: false },
    { label: '1:00 PM', value: '13:00', start: '13:00', end: '14:00', available: false },
    { label: '2:00 PM', value: '14:00', start: '14:00', end: '15:00', available: false },
    { label: '3:00 PM', value: '15:00', start: '15:00', end: '16:00', available: false },
    { label: '4:00 PM', value: '16:00', start: '16:00', end: '17:00', available: false },
    { label: '5:00 PM', value: '17:00', start: '17:00', end: '18:00', available: false },
  ];

  useEffect(() => {
    if (onChange) {
      onChange(availability);
    }
  }, [availability, onChange]);

  const getDaysInMonth = (date: Date): (CalendarDay | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const today = new Date().toISOString().split('T')[0];
    const todayDate = new Date(today + 'T00:00:00');

    const days: (CalendarDay | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dateObj = new Date(dateStr + 'T00:00:00');
      days.push({
        date: dateStr,
        day,
        isToday: dateStr === today,
        isPast: dateObj < todayDate
      });
    }

    return days;
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  const getDayAvailability = (dateStr: string) => {
    return availability.find(day => day.date === dateStr);
  };

  const getAvailabilityStatus = (dateStr: string) => {
    const dayAvailability = getDayAvailability(dateStr);
    if (!dayAvailability) return 'none';
    
    const availableSlots = dayAvailability.timeSlots.filter(slot => slot.available).length;
    if (availableSlots === 0) return 'unavailable';
    if (availableSlots === dayAvailability.timeSlots.length) return 'full';
    return 'partial';
  };

  const toggleTimeSlot = (dateStr: string, timeSlotIndex: number) => {
    if (readOnly) return;

    setAvailability(prev => {
      const updated = [...prev];
      const dayIndex = updated.findIndex(day => day.date === dateStr);
      
      if (dayIndex === -1) {
        // Create new day availability
        const newDay: DayAvailability = {
          date: dateStr,
          timeSlots: timeSlots.map((slot, index) => ({
            ...slot,
            available: index === timeSlotIndex
          }))
        };
        updated.push(newDay);
      } else {
        // Toggle existing slot
        if (updated[dayIndex] && updated[dayIndex]!.timeSlots[timeSlotIndex]) {
          updated[dayIndex]!.timeSlots[timeSlotIndex]!.available = 
            !updated[dayIndex]!.timeSlots[timeSlotIndex]!.available;
        }
      }
      
      return updated;
    });
  };

  const setDayAvailability = (dateStr: string, available: boolean) => {
    if (readOnly) return;

    setAvailability(prev => {
      const updated = [...prev];
      const dayIndex = updated.findIndex(day => day.date === dateStr);
      
      if (dayIndex === -1) {
        // Create new day availability
        const newDay: DayAvailability = {
          date: dateStr,
          timeSlots: timeSlots.map(slot => ({
            ...slot,
            available
          }))
        };
        updated.push(newDay);
      } else {
        // Update all slots for the day
        if (updated[dayIndex]) {
          updated[dayIndex]!.timeSlots.forEach(slot => {
            slot.available = available;
          });
        }
      }
      
      return updated;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Availability Calendar</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-1 hover:bg-gray-100 rounded"
            disabled={readOnly}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-lg font-medium min-w-[200px] text-center">
            {getMonthName(currentMonth)}
          </span>
          <button
            onClick={() => navigateMonth('next')}
            className="p-1 hover:bg-gray-100 rounded"
            disabled={readOnly}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-200 border border-green-400 rounded mr-1"></div>
          <span>Fully Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-200 border border-yellow-400 rounded mr-1"></div>
          <span>Partially Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-200 border border-red-400 rounded mr-1"></div>
          <span>Unavailable</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded mr-1"></div>
          <span>Not Set</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {/* Week day headers */}
        {weekDays.map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {days.map((day, index) => {
          if (!day) {
            return <div key={index} className="p-2"></div>;
          }

          const status = getAvailabilityStatus(day.date);
          const isSelected = selectedDate === day.date;
          
          let bgColor = 'bg-gray-100 border-gray-300';
          if (status === 'full') bgColor = 'bg-green-200 border-green-400';
          else if (status === 'partial') bgColor = 'bg-yellow-200 border-yellow-400';
          else if (status === 'unavailable') bgColor = 'bg-red-200 border-red-400';

          return (
            <button
              key={day.date}
              onClick={() => setSelectedDate(isSelected ? null : day.date)}
              disabled={day.isPast || readOnly}
              className={`p-2 border rounded text-sm relative ${bgColor} ${
                isSelected ? 'ring-2 ring-blue-500' : ''
              } ${day.isPast ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'} ${
                day.isToday ? 'font-bold' : ''
              }`}
            >
              {day.day}
              {day.isToday && (
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Time Slots for Selected Day */}
      {selectedDate && !readOnly && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">
              {new Date(selectedDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h4>
            <div className="space-x-2">
              <button
                onClick={() => setDayAvailability(selectedDate, true)}
                className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200"
              >
                Mark All Available
              </button>
              <button
                onClick={() => setDayAvailability(selectedDate, false)}
                className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
              >
                Mark All Unavailable
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            {timeSlots.map((slot, index) => {
              const dayAvailability = getDayAvailability(selectedDate);
              const isAvailable = dayAvailability?.timeSlots[index]?.available || false;
              
              return (
                <button
                  key={slot.value}
                  onClick={() => toggleTimeSlot(selectedDate, index)}
                  className={`p-2 text-sm border rounded transition-colors ${
                    isAvailable
                      ? 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {slot.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Read-only selected day info */}
      {selectedDate && readOnly && (
        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-3">
            {new Date(selectedDate).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </h4>
          
          <div className="grid grid-cols-3 gap-2">
            {timeSlots.map((slot, index) => {
              const dayAvailability = getDayAvailability(selectedDate);
              const isAvailable = dayAvailability?.timeSlots[index]?.available || false;
              
              return (
                <div
                  key={slot.value}
                  className={`p-2 text-sm border rounded ${
                    isAvailable
                      ? 'bg-green-100 border-green-300 text-green-800'
                      : 'bg-gray-100 border-gray-300 text-gray-500'
                  }`}
                >
                  {slot.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityCalendar;